import React, { useContext, useState, useEffect } from 'react';
import { UserContext } from '../UserContext';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../ApiContext';
import TextInput from '../components/TextInput';
import FormActionButton from '../components/FormActionButton';
import { states } from '../lib/states';
import Container from '../components/Container';
import Heading2 from '../components/Heading2';
import { CreateCustomerRequest } from '../apiClient/createCustomer';
import { ApiErrorAlert, ApiError } from '../components/ApiErrorAlert';

const PersonalAddressScreen: React.FC = () => {
  const userContext = useContext(UserContext);
  const api = useApi();
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [stateValue, setStateValue] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [countryId, setCountryId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<ApiError | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (userContext) {
      const { address_1, address_2, city, state, zip_code, country_id } = userContext.userData.homeAddress || {};
      setAddress1(address_1 || '');
      setAddress2(address_2 || '');
      setCity(city || '');
      setStateValue(state || '');
      setZipcode(zip_code || '');
      setCountryId(country_id || '225' /* United States */);
    }
  }, [userContext]);

  if (!userContext) return null;

  const { userData, setUserData } = userContext;

  const handleNext = async () => {
    // Update userData with homeAddress
    setUserData({
      ...userData,
      homeAddress: {
        address_1: address1,
        address_2: address2,
        city,
        state: stateValue,
        zip_code: zipcode,
        country_id: countryId,
      },
    });

    const { provider_id: providerId, email, password, firstName, lastName, phoneNumber, birthDate } = userData;

    if (!providerId) {
      console.debug('No provider selected');
      return;
    }

    setIsLoading(true);

    // Construct the request data
    const requestData: CreateCustomerRequest = {
      provider_id: providerId,
      customer: {
        first_name: firstName,
        last_name: lastName,
        email: email,
        phone: phoneNumber,
        date_of_birth: birthDate,
      },
      password: password,
      address: {
        address_1: address1,
        address_2: address2,
        city,
        state: stateValue,
        zip_code: zipcode,
      },
    };

    const { data, error } = await api.client.createCustomer(requestData);

    if (error) {
      setApiError(error);
    } else if (data?.customer_id) {
      setUserData({ ...userData, customer_id: data.customer_id });
      navigate('/billing-address');
    } else {
      // The API is built to return an error when no customer_id is returned, so this
      // unknown error path is highly unlikely.
      alert(`An unknown error has occurred when trying to create your guest account.`);
    }
    setIsLoading(false);
  };

  return (
    <Container>
      <Heading2>What's Your Address?</Heading2>
      <p className="text-gray-700 mb-6">
        This is address where your Skin Clique provider will meet you for your treatment.
      </p>
      <div className="space-y-6">
        <TextInput
          label="Address Line 1"
          value={address1}
          onChange={(e) => setAddress1(e.target.value)}
          placeholder="123 Main St"
        />
        <TextInput
          label="Address Line 2"
          value={address2}
          onChange={(e) => setAddress2(e.target.value)}
          placeholder="Apt, Suite, etc."
        />
        <TextInput label="City" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
        <label className="block">
          <span className="text-lg text-gray-700">State</span>
          <select
            value={stateValue}
            onChange={(e) => setStateValue(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          >
            {states.map((state, index) => (
              <option value={state.short_name} key={state.id}>
                {state.name}
              </option>
            ))}
          </select>
        </label>
        <TextInput label="Zip Code" value={zipcode} onChange={(e) => setZipcode(e.target.value)} placeholder="12345" />
      </div>
      <div className="flex justify-center mt-8">
        <FormActionButton onClick={handleNext} isLoading={isLoading}>
          Next
        </FormActionButton>
      </div>
      <ApiErrorAlert screenName="personal address screen" apiError={apiError} clearError={setApiError} />
    </Container>
  );
};

export default PersonalAddressScreen;
